import { c as _c } from "react/compiler-runtime";
import { DialogAction } from "@catchy/ui";
import { useMemo, useState } from "react";
import Link from "next/link";
import useSWR from "swr";
import { MenuItem } from "@headlessui/react";
import { OptionsMenu } from "@catchy/ui";
import Spinner from "@catchy/assets/svgs/spinner.svg";
import SnapperCard from "@/svgs/snapper-card.svg";
import Bus from "@catchy/assets/svgs/pass/pass-bus.svg";
import Train from "@catchy/assets/svgs/pass/pass-train.svg";
import CableCar from "@catchy/assets/svgs/pass/pass-cablecar.svg";
import { useForm } from "react-hook-form";
import useLocalStorage from "hooks/useLocalStorage";
import { Modal } from "@catchy/ui";
import { isValidSnapperCardNumber } from "@/lib/snapperCard";
const isInvalidSnapperDetails = data => data?.error && data.errorCode === "INVALID_DETAILS";
const getSnapperLoadErrorMessage = data => {
  if (!data?.error) {
    return null;
  }
  if (isInvalidSnapperDetails(data)) {
    return "Unable to load Snapper balance: Card details not valid.";
  }
  return "Unable to load Snapper balance.";
};
const SvgIcon = t0 => {
  const $ = _c(2);
  const {
    icon
  } = t0;
  const Icon = icon;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="h-5 w-5" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const simplePluralise = (number, string) => {
  const concat = number + " " + string;
  if (Math.abs(number) === 1) {
    return concat;
  } else {
    return concat + "s";
  }
};
const transportTypeTranslator = type => {
  switch (type) {
    case "bus":
      return "BUS";
    case "train":
      return "TRAIN";
    case "cable_car":
      return "CABLE_CAR";
    default:
      break;
  }
};
const iconTranslator = type => {
  switch (type) {
    case "bus":
      return {
        icon: Bus,
        color: "text-orange-400 dark:text-amber-400"
      };
    case "train":
      return {
        icon: Train,
        color: "text-green-600 dark:text-green-400"
      };
    case "cable_car":
      return {
        icon: CableCar,
        color: "text-red-600 dark:text-red-400"
      };
    default:
      break;
  }
};
const durationTranslator = (type, remaining) => {
  switch (type) {
    case "DAYS":
      return simplePluralise(remaining, "day");
    case "TRIPS":
      return simplePluralise(remaining, "trip");
    default:
      break;
  }
};
const checkSnapperNumber = cardNumber => isValidSnapperCardNumber(cardNumber) ? true : "Invalid card number";
const formatCurrencyNZD = cents => new Intl.NumberFormat("en-NZ", {
  style: "currency",
  currency: "NZD"
}).format(cents * 0.01);
const showBalance = data => {
  const balanceCents = Number(data.currentBalanceCents);
  if (Number.isNaN(balanceCents)) {
    return;
  }
  const iouCents = Number(data.iouCents);
  const hasIou = !Number.isNaN(iouCents) && iouCents > 0;
  const effectiveBalanceCents = hasIou ? balanceCents - iouCents : balanceCents;
  const isNegativeBalance = effectiveBalanceCents < 0;
  return <div className="shrink-0 text-right">
      <div className={isNegativeBalance ? "font-semibold text-red-600 dark:text-red-400" : ""}>
        {formatCurrencyNZD(effectiveBalanceCents)}
      </div>
    </div>;
};
const PassRow = t0 => {
  const $ = _c(22);
  const {
    pass,
    type
  } = t0;
  let t1;
  if ($[0] !== type) {
    t1 = iconTranslator(type);
    $[0] = type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `grid h-[1.125rem] w-11 shrink-0 place-content-center pr-0.5 pt-0.5 ${t1.color}`;
  let t3;
  if ($[2] !== type) {
    t3 = iconTranslator(type);
    $[2] = type;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3.icon) {
    t4 = <SvgIcon icon={t3.icon} />;
    $[4] = t3.icon;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== pass.name) {
    t6 = <div>{pass.name}</div>;
    $[9] = pass.name;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== pass.durationRemaining || $[12] !== pass.durationType) {
    t7 = durationTranslator(pass.durationType, pass.durationRemaining);
    $[11] = pass.durationRemaining;
    $[12] = pass.durationType;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <div className="shrink-0">{t7}</div>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = <div className="flex w-full flex-1 justify-between gap-x-4 leading-tight">{t6}{t8}</div>;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t9) {
    t10 = <div className="flex w-full text-lg">{t5}{t9}</div>;
    $[19] = t5;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};
const SnapperWidget = t0 => {
  const $ = _c(95);
  const {
    isApple,
    isClient,
    type
  } = t0;
  const [snapperCard, setSnapperCard] = useLocalStorage("snapper_card", null);
  let t1;
  if ($[0] !== snapperCard) {
    t1 = snapperCard && typeof snapperCard === "object" ? snapperCard : {};
    $[0] = snapperCard;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const snapperCardDetails = t1;
  const snapperCardNumber = snapperCardDetails.number || "";
  const snapperCardCvv = snapperCardDetails.cvv || "";
  const handleAfterEnter = () => {
    window.setTimeout(() => {
      setFocus("number");
    }, 150);
  };
  let t2;
  if ($[2] !== snapperCard) {
    t2 = {
      defaultValues: snapperCard
    };
    $[2] = snapperCard;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    register,
    handleSubmit,
    reset,
    setFocus: t3,
    formState: t4
  } = useForm(t2);
  const setFocus = t3;
  const {
    errors
  } = t4;
  const [snapperModalIsOpen, setSnapperModalIsOpen] = useState(false);
  let t5;
  bb0: {
    if (!snapperCardNumber || !snapperCardCvv) {
      t5 = null;
      break bb0;
    }
    let t6;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = crypto.randomUUID();
      $[4] = t6;
    } else {
      t6 = $[4];
    }
    t5 = `snapper-card-${t6}`;
  }
  const snapperRequestKey = t5;
  let t6;
  if ($[5] !== snapperCardCvv || $[6] !== snapperCardNumber) {
    t6 = () => fetch("/api/snapper", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        cardNumber: snapperCardNumber,
        cvv: snapperCardCvv
      })
    }).then(_temp);
    $[5] = snapperCardCvv;
    $[6] = snapperCardNumber;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const loadSnapperCard = t6;
  let t7;
  if ($[8] !== snapperCardCvv || $[9] !== snapperCardNumber || $[10] !== snapperRequestKey) {
    t7 = snapperCardNumber && snapperCardCvv && checkSnapperNumber(snapperCardNumber) === true ? snapperRequestKey : null;
    $[8] = snapperCardCvv;
    $[9] = snapperCardNumber;
    $[10] = snapperRequestKey;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      refreshInterval: 300000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      shouldRetryOnError: false,
      dedupingInterval: 120000
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const {
    data,
    isLoading
  } = useSWR(t7, loadSnapperCard, t8);
  let t9;
  if ($[13] !== data || $[14] !== type) {
    t9 = data?.passes?.length && data.passes.filter(e => e.transportType === transportTypeTranslator(type) && (e.status === "ACTIVE" || e.status === "NEW"));
    $[13] = data;
    $[14] = type;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const passes = t9;
  if (isLoading || !isClient) {
    let t10;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="mx-[20px] mb-2 flex items-center justify-between gap-x-2"><h2 className="text-3xl font-semibold">Snapper</h2></div>;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="grid h-6 w-11 shrink-0 place-content-center pr-0.5"><Spinner className="h-6 w-6 animate-spin text-zinc-300 opacity-60" /></div>;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    let t12;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className="-mb-2 flex flex-col">{t10}<div className="flex flex-col items-start gap-y-5 px-[20px] py-5 text-lg leading-tight outline-none ring-blue-500/50 transition-colors duration-150 hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10 sm:rounded-lg"><div className="flex w-full text-lg">{t11}<div className="flex w-full justify-between gap-x-4 leading-tight"><div className="flex-1">Loading...</div><div className="shrink-0" /></div></div></div></div>;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    return t12;
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h2 className="text-3xl font-semibold">Snapper</h2>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== isApple || $[21] !== isLoading || $[22] !== reset || $[23] !== snapperCard) {
    t11 = snapperCard?.number && !isLoading ? <OptionsMenu isApple={isApple}>{snapperCard?.number && <MenuItem as="button" className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3" onClick={() => {
        setSnapperModalIsOpen(true);
        reset(snapperCard || {
          number: "",
          cvv: ""
        });
      }}>Edit Snapper Settings…</MenuItem>}</OptionsMenu> : !isLoading && !snapperCard?.number && <button onClick={() => {
      setSnapperModalIsOpen(true);
      reset({
        number: "",
        cvv: ""
      });
    }} className="pointer-events-auto relative top-1.5 h-9 items-center rounded-full px-3 font-bold text-blue-500 outline-none ring-blue-500/50 transition-all duration-200 hover:bg-zinc-400/10 focus-visible:ring active:bg-zinc-400/10">Add</button>;
    $[20] = isApple;
    $[21] = isLoading;
    $[22] = reset;
    $[23] = snapperCard;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <div className="mx-[20px] mb-2 flex items-center justify-between gap-x-2">{t10}{t11}</div>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== data || $[28] !== passes || $[29] !== snapperCard?.cvv || $[30] !== snapperCard?.number || $[31] !== type) {
    t13 = snapperCard?.number && data && !data.error && !data.blocked && !data.permanentBlock ? <Link href="/transactions" className="flex flex-col items-start gap-y-5 px-[20px] py-5 text-lg leading-tight outline-none ring-blue-500/50 transition-colors duration-150 hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10 sm:rounded-lg"><div className="flex w-full text-lg"><div className="grid h-6 w-11 shrink-0 place-content-center pb-0.5 pr-0.5"><SnapperCard className={`h-auto w-7 ${data.concession === "Child" ? "text-green-600" : "text-red-600"}`} /></div><div className="flex w-full justify-between gap-x-4 leading-tight"><div className="flex-1">Balance</div>{showBalance(data)}</div></div>{passes?.length > 0 && passes.map((pass, key) => <PassRow pass={pass} key={pass.slot} type={type} />)}</Link> : <div className="flex cursor-pointer flex-col items-start gap-y-5 px-[20px] py-5 text-lg leading-tight outline-none ring-blue-500/50 transition-colors duration-150 hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10 sm:rounded-lg" onClick={() => setSnapperModalIsOpen(true)}><div className="flex w-full text-lg"><div className="grid h-6 w-11 shrink-0 place-content-center pb-0.5 pr-0.5"><SnapperCard className="-mt-px h-auto w-7 text-zinc-400 opacity-50" /></div><div className="flex w-full justify-between gap-x-4 leading-tight"><div className="flex-1">{!snapperCard?.number || !snapperCard?.cvv ? "Add your Snapper details" : getSnapperLoadErrorMessage(data)}</div><div className="shrink-0" /></div></div></div>;
    $[27] = data;
    $[28] = passes;
    $[29] = snapperCard?.cvv;
    $[30] = snapperCard?.number;
    $[31] = type;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== data) {
    t14 = data && (data.blocked || data.permanentBlock) && <div className="mx-[20px] py-2 text-lg opacity-80">Card blocked.</div>;
    $[33] = data;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <div>{t13}{t14}</div>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setSnapperModalIsOpen(false);
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = snapperCard ? "Snapper Settings" : "Add Snapper";
  let t18;
  if ($[39] !== setSnapperCard) {
    t18 = e_0 => {
      ;
      try {
        setSnapperModalIsOpen(false);
        setSnapperCard(e_0);
      } catch (t19) {
        const e_1 = t19;
        console.error(e_1);
      }
    };
    $[39] = setSnapperCard;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== handleSubmit || $[42] !== t18) {
    t19 = handleSubmit(t18);
    $[41] = handleSubmit;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <label htmlFor="snapperNumber" className="font-semibold">Card number</label>;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== register) {
    let t22;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = /^\d{4}[- ]?\d{4}[- ]?\d{4}[- ]?\d{4}$/;
      $[47] = t22;
    } else {
      t22 = $[47];
    }
    t21 = register("number", {
      required: {
        value: true,
        message: "Required"
      },
      pattern: {
        value: t22,
        message: "Must be 16 digits"
      },
      validate: _temp2
    });
    $[45] = register;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const t22 = errors.number ? "true" : "false";
  let t23;
  if ($[48] !== t21 || $[49] !== t22) {
    t23 = <input id="snapperNumber" type="text" inputMode="numeric" enterKeyHint="next" className="transition-colors h-12 w-full appearance-none rounded border-none bg-transparent px-2.5 py-2 text-lg placeholder-zinc-400 outline-none ring-1 ring-zinc-500/50 duration-100 focus:ring focus:ring-blue-500/50" placeholder="1010 0000 XXXX XXXX" {...t21} onInput={_temp3} aria-invalid={t22} autoComplete="false" autoCorrect="false" />;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== errors.number) {
    t24 = errors.number && <span>{errors.number.message}</span>;
    $[51] = errors.number;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t23 || $[54] !== t24) {
    t25 = <div className="flex basis-3/4 flex-col gap-y-1">{t20}{t23}{t24}</div>;
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <label htmlFor="snapperCvv" className="font-semibold">CVV</label>;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== register) {
    let t28;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = /^\d{3}$/g;
      $[59] = t28;
    } else {
      t28 = $[59];
    }
    t27 = register("cvv", {
      required: {
        value: true,
        message: "Required"
      },
      pattern: {
        value: t28,
        message: "3 digits"
      }
    });
    $[57] = register;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  const t28 = errors.cvv ? "true" : "false";
  let t29;
  if ($[60] !== t27 || $[61] !== t28) {
    t29 = <input id="snapperCvv" type="text" inputMode="numeric" enterKeyHint="done" className="transition-colors h-12 w-full appearance-none rounded border-none bg-transparent px-2.5 text-lg placeholder-zinc-400 outline-none ring-1 ring-zinc-500/50 duration-100 focus:ring focus:ring-blue-500/50" placeholder="XXX" {...t27} aria-invalid={t28} autoComplete="false" autoCorrect="false" />;
    $[60] = t27;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== errors.cvv) {
    t30 = errors.cvv && <span>{errors.cvv.message}</span>;
    $[63] = errors.cvv;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t29 || $[66] !== t30) {
    t31 = <div className="flex basis-1/4 flex-col gap-y-1">{t26}{t29}{" "}{t30}</div>;
    $[65] = t29;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t25 || $[69] !== t31) {
    t32 = <div className="flex flex-col gap-y-4"><div className="px-[20px]"><div className="flex gap-x-4">{t25}{t31}</div></div></div>;
    $[68] = t25;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <DialogAction type="submit">Save</DialogAction>;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== reset || $[73] !== snapperCard) {
    t34 = <DialogAction onClick={() => {
      setSnapperModalIsOpen(false);
      reset(snapperCard);
    }} type="button">Cancel</DialogAction>;
    $[72] = reset;
    $[73] = snapperCard;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="flex-1" />;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== reset || $[77] !== setSnapperCard) {
    t36 = <DialogAction onClick={() => {
      setSnapperCard(null);
      reset({
        number: "",
        cvv: ""
      });
      setSnapperModalIsOpen(false);
    }} destructive={true} type="button">Clear</DialogAction>;
    $[76] = reset;
    $[77] = setSnapperCard;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t34 || $[80] !== t36) {
    t37 = <div className="flex flex-row-reverse gap-2 p-3 font-semibold text-blue-500 dark:text-white">{t33}{t34}{t35}{t36}</div>;
    $[79] = t34;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== t19 || $[83] !== t32 || $[84] !== t37) {
    t38 = <form onSubmit={t19}>{t32}{t37}</form>;
    $[82] = t19;
    $[83] = t32;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== handleAfterEnter || $[87] !== snapperModalIsOpen || $[88] !== t17 || $[89] !== t38) {
    t39 = <Modal isOpen={snapperModalIsOpen} onClose={t16} title={t17} afterEnter={handleAfterEnter}>{t38}</Modal>;
    $[86] = handleAfterEnter;
    $[87] = snapperModalIsOpen;
    $[88] = t17;
    $[89] = t38;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  let t40;
  if ($[91] !== t12 || $[92] !== t15 || $[93] !== t39) {
    t40 = <div className="-mb-2 flex flex-col">{t12}{t15}{t39}</div>;
    $[91] = t12;
    $[92] = t15;
    $[93] = t39;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  return t40;
};
export default SnapperWidget;
function _temp(res) {
  return res.json();
}
function _temp2(v) {
  return checkSnapperNumber(v);
}
function _temp3(event) {
  const {
    value
  } = event.target;
  event.target.value = value.replace(/\D/g, "").replace(/(\d{4})/g, "$1 ").trim();
}